import React, { useEffect, useState } from 'react';
import { Clock, Mail, Save, Eye, AlertCircle, Check, Info, X } from 'lucide-react';
import { useAuth } from '../auth/KeycloakContext';
import InlineEditField from '../components/InlineEditField';

interface TrialConfig {
  trial_duration_days: number;
  updated_at: string | null;
}

interface Placeholder {
  key: string;
  description: string;
}

interface EmailTemplate {
  id: string;
  template_type: string;
  name: string;
  description: string;
  subject: string;
  body_html: string;
  placeholders: Placeholder[];
  is_active: boolean;
}

const TEMPLATE_OPTIONS = [
  { value: 'user_invitation', label: 'Email an Benutzer' },
  { value: 'tenant_verification', label: 'Verify-Email an Tenant Administrator' },
  { value: 'tenant_welcome', label: 'Willkommensemail an Tenant Administrator' },
];

type TabType = 'general' | 'email-templates';

export default function TrialPage() {
  const { keycloak } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('general');

  // Trial config state
  const [config, setConfig] = useState<TrialConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Email template state
  const [selectedTemplate, setSelectedTemplate] = useState<string>('user_invitation');
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch trial config
  const fetchConfig = async () => {
    if (!keycloak?.token) return;
    try {
      const res = await fetch('/api/admin/trial-config', {
        headers: { 'Authorization': `Bearer ${keycloak.token}` }
      });
      if (!res.ok) throw new Error('Fehler beim Laden der Konfiguration');
      const data = await res.json();
      setConfig(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [keycloak?.token]);

  const handleSaveTrialDuration = async (value: string | number): Promise<void> => {
    const res = await fetch('/api/admin/trial-config', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak?.token}`
      },
      body: JSON.stringify({ trial_duration_days: Number(value) })
    });

    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.detail || 'Fehler beim Speichern');
    }

    const data = await res.json();
    setConfig(data);
  };

  // Load email template when selection changes or tab changes
  useEffect(() => {
    if (activeTab === 'email-templates' && keycloak?.token) {
      loadTemplate(selectedTemplate);
    }
  }, [selectedTemplate, activeTab, keycloak?.token]);

  const loadTemplate = async (templateType: string) => {
    setLoadingTemplate(true);
    setMessage(null);
    try {
      const response = await fetch(`/api/email-templates/${templateType}`, {
        headers: {
          'Authorization': `Bearer ${keycloak?.token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTemplate(data);
        setSubject(data.subject);
        setBodyHtml(data.body_html);
      } else {
        setMessage({ type: 'error', text: 'Fehler beim Laden des Templates' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Netzwerkfehler beim Laden' });
    } finally {
      setLoadingTemplate(false);
    }
  };

  const saveTemplate = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(`/api/email-templates/${selectedTemplate}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${keycloak?.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject,
          body_html: bodyHtml,
        }),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Template erfolgreich gespeichert' });
        await loadTemplate(selectedTemplate);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Fehler beim Speichern' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Netzwerkfehler beim Speichern' });
    } finally {
      setSaving(false);
    }
  };

  const showPreviewHandler = async () => {
    setMessage(null);
    try {
      const response = await fetch(`/api/email-templates/${selectedTemplate}/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${keycloak?.token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.body_html);
        setShowPreview(true);
      } else {
        setMessage({ type: 'error', text: 'Fehler beim Erstellen der Vorschau' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Netzwerkfehler' });
    }
  };

  const tabs = [
    { id: 'general' as TabType, label: 'Allgemein', icon: Clock },
    { id: 'email-templates' as TabType, label: 'E-Mail Vorlagen', icon: Mail },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Trial</h1>
        <p className="text-slate-500 mt-1">Konfiguration der Testphase</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-sayses-accent text-sayses-accent'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content: Allgemein */}
      {activeTab === 'general' && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-6">Allgemeine Einstellungen</h2>

            {loading ? (
              <div className="text-slate-500">Laden...</div>
            ) : error ? (
              <div className="text-red-600">{error}</div>
            ) : config ? (
              <div className="space-y-6">
                <div className="grid grid-cols-3 items-center gap-4">
                  <label className="text-slate-600">Dauer der Testphase (Tage)</label>
                  <div className="col-span-2">
                    <InlineEditField
                      value={config.trial_duration_days}
                      onSave={handleSaveTrialDuration}
                      type="number"
                      suffix="Tage"
                    />
                  </div>
                </div>

                {config.updated_at && (
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-sm text-slate-400">
                      Zuletzt aktualisiert: {new Date(config.updated_at).toLocaleString('de-DE')}
                    </p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Tab Content: E-Mail Vorlagen */}
      {activeTab === 'email-templates' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            {/* Template Selector */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-slate-700">Template:</label>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="flex-1 max-w-md px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent"
              >
                {TEMPLATE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loadingTemplate ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-sayses-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Lade Template...</p>
            </div>
          ) : template ? (
            <div className="p-6 space-y-6">
              {/* Template Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-800 mb-1">{template.name}</h3>
                <p className="text-sm text-slate-500">{template.description}</p>
              </div>

              {/* Message */}
              {message && (
                <div
                  className={`flex items-center gap-2 p-4 rounded-lg ${
                    message.type === 'success'
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {message.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
                  {message.text}
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Betreff
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent"
                  placeholder="Email Betreff..."
                />
              </div>

              {/* Body HTML */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Inhalt (HTML)
                </label>
                <textarea
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  rows={20}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sayses-accent focus:border-sayses-accent font-mono text-sm"
                  placeholder="HTML Content..."
                />
              </div>

              {/* Placeholders Info */}
              {template.placeholders && template.placeholders.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-start gap-2">
                    <Info size={18} className="text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-800 mb-2">Verfügbare Platzhalter</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {template.placeholders.map((ph) => (
                          <div key={ph.key} className="text-sm">
                            <code className="bg-blue-100 px-1.5 py-0.5 rounded text-blue-700">
                              {`{{${ph.key}}}`}
                            </code>
                            <span className="text-blue-600 ml-2">{ph.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
                <button
                  onClick={saveTemplate}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-sayses-accent hover:bg-sayses-accent-hover text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Speichern...' : 'Speichern'}
                </button>
                <button
                  onClick={showPreviewHandler}
                  className="flex items-center gap-2 px-6 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <Eye size={18} />
                  Vorschau
                </button>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-slate-500">
              Kein Template geladen
            </div>
          )}
        </div>
      )}

      {/* Email Preview Modal */}
      {showPreview && previewHtml && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-800">Email Vorschau</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-1 text-slate-500 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <iframe
                srcDoc={previewHtml}
                className="w-full h-full min-h-[600px] border border-slate-200 rounded-lg"
                title="Email Preview"
              />
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setShowPreview(false)}
                className="px-6 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
